import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatUTCTime, formatYearMonthToLabel } from "../../../global/utils/unixToLocalTime";

const BRAND = [0, 61, 211];
const BRAND_DARK = [8, 28, 92];
const ACCENT = [191, 207, 244];
const INK = [18, 24, 38];
const MUTED = [90, 102, 122];
const LINE = [220, 226, 236];
const PAID = [15, 110, 86];
const UNPAID = [0, 61, 211];
const OVERDUE = [154, 51, 36];

const money = (value) => {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const generatedStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const invoiceStatus = (row = {}) => {
  if (row.PaymentStatus) return row.PaymentStatus;
  if (row.PaymentNo || row.Paid) return "Paid";
  const month = row.Month;
  if (month && String(month) < new Date().toISOString().slice(0, 7)) {
    return "Overdue";
  }
  return "Unpaid";
};

const statusColor = (status) => {
  if (status === "Paid") return PAID;
  if (status === "Overdue") return OVERDUE;
  return UNPAID;
};

export const buildInvoiceFilterSummary = (params = {}) => {
  const parts = [];
  if (params.Month) parts.push(`Month: ${formatYearMonthToLabel(params.Month)}`);
  if (params.OemNo) parts.push(`OEM: ${params.OemName || params.OemNo}`);
  if (params.DealerNo) parts.push(`Dealer: ${params.DealerName || params.DealerNo}`);
  if (params.PaymentStatus) {
    parts.push(
      `Status: ${
        params.PaymentStatus.charAt(0).toUpperCase() + params.PaymentStatus.slice(1)
      }`,
    );
  }
  if (params.InvoiceNo) parts.push(`Invoice: ${params.InvoiceNo}`);
  if (params.InvoiceTime) parts.push("Invoice time filter applied");
  if (params.selected) parts.push("Selected invoices only");
  return parts.join("   ·   ");
};

const drawHeader = (doc, title, subtitle, pageWidth) => {
  doc.setFillColor(...BRAND_DARK);
  doc.rect(0, 0, pageWidth, 8, "F");
  doc.setFillColor(...BRAND);
  doc.rect(0, 8, pageWidth, 24, "F");
  doc.setFillColor(...ACCENT);
  doc.rect(0, 32, pageWidth, 2.2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, 14, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(subtitle, 14, 28);
  doc.setFontSize(8);
  doc.text("Jupiter Billing", pageWidth - 14, 20, { align: "right" });
  doc.text(generatedStamp(), pageWidth - 14, 28, { align: "right" });
  doc.setTextColor(...INK);
};

const drawKpiBoxes = (doc, boxes, y) => {
  const width = 64;
  const gap = 6;
  boxes.forEach((box, index) => {
    const x = 14 + index * (width + gap);
    doc.setFillColor(box.fill[0], box.fill[1], box.fill[2]);
    doc.setDrawColor(...LINE);
    doc.roundedRect(x, y, width, 18, 2, 2, "FD");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(box.label, x + 4, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(String(box.value), x + 4, y + 14);
    doc.setFont("helvetica", "normal");
  });
};

const addPageNumbers = (doc, footerLabel) => {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...LINE);
    doc.line(14, pageHeight - 10, pageWidth - 14, pageHeight - 10);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(footerLabel, 14, pageHeight - 5);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 5, {
      align: "right",
    });
  }
};

export const generateInvoiceRegisterPDF = (rows = [], filterParams = {}) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const monthLabel = filterParams.Month
    ? formatYearMonthToLabel(filterParams.Month)
    : "All periods";
  const scope = filterParams.selected ? "Selected invoices" : "Filtered invoices";

  drawHeader(doc, "INVOICE REGISTER", `${scope}  ·  ${monthLabel}`, pageWidth);

  const total = rows.reduce((sum, row) => sum + Number(row.Amount || 0), 0);
  const paid = rows.filter((row) => invoiceStatus(row) === "Paid").length;
  const unpaid = rows.filter((row) => invoiceStatus(row) === "Unpaid").length;
  const overdue = rows.filter((row) => invoiceStatus(row) === "Overdue").length;
  const paidAmount = rows
    .filter((row) => invoiceStatus(row) === "Paid")
    .reduce((sum, row) => sum + Number(row.Amount || 0), 0);

  drawKpiBoxes(
    doc,
    [
      { label: "INVOICES", value: rows.length, fill: [245, 248, 252] },
      { label: "TOTAL AMOUNT", value: money(total), fill: [237, 242, 255] },
      { label: "COLLECTED", value: money(paidAmount), fill: [232, 247, 241] },
      { label: "OPEN / OVERDUE", value: `${unpaid} / ${overdue}`, fill: [255, 246, 238] },
    ],
    40,
  );

  const filterSummary = buildInvoiceFilterSummary(filterParams);
  let tableStartY = 64;
  if (filterSummary) {
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    const lines = doc.splitTextToSize(filterSummary, pageWidth - 28);
    doc.text(lines, 14, 64);
    tableStartY = 64 + lines.length * 4.5 + 4;
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [["No", "Invoice Time", "Dealer", "OEM", "Month", "Amount", "Status", "Payment Time"]],
    body: rows.map((row) => [
      row.InvoiceNo ?? "",
      row.InvoiceTime ? formatUTCTime(row.InvoiceTime) : "",
      row.DealerName || "—",
      row.OemName || "—",
      row.Month ? formatYearMonthToLabel(row.Month) : "",
      money(row.Amount),
      invoiceStatus(row),
      row.PaymentTime ? formatUTCTime(row.PaymentTime) : "—",
    ]),
    foot: [["", "", "", "", "Total", money(total), `${paid} paid`, ""]],
    styles: {
      fontSize: 8,
      cellPadding: 2.2,
      textColor: INK,
      lineColor: LINE,
      lineWidth: 0.15,
      valign: "middle",
    },
    headStyles: {
      fillColor: BRAND,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
    },
    footStyles: {
      fillColor: [237, 242, 255],
      textColor: INK,
      fontStyle: "bold",
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: [247, 249, 252] },
    columnStyles: {
      0: { cellWidth: 18 },
      5: { halign: "right", cellWidth: 28 },
      6: { cellWidth: 24, halign: "center" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 6) {
        data.cell.styles.textColor = statusColor(data.cell.raw);
        data.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: 14, right: 14, bottom: 14 },
    showHead: "everyPage",
    showFoot: "lastPage",
  });

  addPageNumbers(doc, "Confidential  ·  Invoice register");
  return doc;
};

export const generateInvoiceStatementPDF = (invoice = {}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const items = invoice.InvoiceItems || [];
  const status = invoiceStatus(invoice);
  const monthLabel = invoice.Month
    ? formatYearMonthToLabel(invoice.Month)
    : "Invoice statement";

  drawHeader(doc, `INVOICE  #${invoice.InvoiceNo || ""}`, monthLabel, pageWidth);

  doc.setFillColor(247, 249, 252);
  doc.setDrawColor(...LINE);
  doc.roundedRect(14, 40, 108, 26, 2, 2, "FD");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("BILL TO", 18, 47);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text(invoice.DealerName || "Dealer", 18, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(invoice.OemName || "—", 18, 61);

  doc.setFillColor(237, 242, 255);
  doc.roundedRect(128, 40, 68, 26, 2, 2, "F");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("INVOICE DATE", 132, 47);
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(invoice.InvoiceTime ? formatUTCTime(invoice.InvoiceTime) : "—", 132, 53);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("STATUS", 132, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...statusColor(status));
  doc.text(status.toUpperCase(), 132, 65);
  doc.setFont("helvetica", "normal");

  autoTable(doc, {
    startY: 74,
    head: [["Order", "Item", "Quantity", "Device", "Period", "Amount"]],
    body: items.length
      ? items.map((item) => [
          item.OrderNo ?? "",
          item.LicenseItem || "—",
          item.NewCode ?? "—",
          item.Mac || "—",
          item.Period || "—",
          money(item.Price),
        ])
      : [["—", "No line items", "—", "—", "—", money(invoice.Amount)]],
    styles: {
      fontSize: 9,
      cellPadding: 2.6,
      textColor: INK,
      lineColor: LINE,
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: BRAND,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [247, 249, 252] },
    columnStyles: {
      5: { halign: "right" },
    },
    margin: { left: 14, right: 14, bottom: 28 },
    showHead: "everyPage",
  });

  const endY = doc.lastAutoTable?.finalY || 90;
  doc.setFillColor(...BRAND);
  doc.roundedRect(120, endY + 8, 76, 22, 2, 2, "F");
  doc.setFontSize(8);
  doc.setTextColor(191, 207, 244);
  doc.text(status === "Paid" ? "AMOUNT PAID" : "AMOUNT DUE", 126, endY + 16);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(money(invoice.Amount), 190, endY + 24, { align: "right" });
  doc.setFont("helvetica", "normal");

  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(
    status === "Paid"
      ? "This invoice has been paid. Thank you for your business."
      : "Please settle this invoice at your earliest convenience. Thank you.",
    14,
    endY + 18,
  );

  addPageNumbers(doc, `Confidential  ·  Invoice #${invoice.InvoiceNo || ""}`);
  return doc;
};

export const downloadInvoiceRegisterPDF = (rows, filterParams, filename) => {
  generateInvoiceRegisterPDF(rows, filterParams).save(
    filename || `Invoice-Register_${new Date().toISOString().slice(0, 10)}.pdf`,
  );
};

export const printInvoiceRegisterPDF = (rows, filterParams) => {
  const doc = generateInvoiceRegisterPDF(rows, filterParams);
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

export const downloadInvoiceStatementPDF = (invoice, filename) => {
  generateInvoiceStatementPDF(invoice).save(
    filename || `Invoice_${invoice.InvoiceNo || "statement"}.pdf`,
  );
};

export const printInvoiceStatementPDF = (invoice) => {
  const doc = generateInvoiceStatementPDF(invoice);
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};
